// Hand-authored SVG path data for shapes that aren't simple regular
// polygons/stars, all in a shared 0 0 100 100 coordinate box.

export const HEART_PATH =
  'M50,88 C20,62 5,42 5,25 C5,10 18,0 30,0 C40,0 47,7 50,15 C53,7 60,0 70,0 C82,0 95,10 95,25 C95,42 80,62 50,88 Z';

export const CLOUD_PATH =
  'M22,72 C7,72 5,52 20,49 C17,30 42,24 52,36 C58,20 82,21 84,39 C99,39 99,61 86,65 C89,76 74,80 68,73 C58,82 30,81 22,72 Z';

export const TEARDROP_PATH =
  'M50,3 C68,30 90,52 90,70 A40,40 0 1 1 10,70 C10,52 32,30 50,3 Z';

export const LIGHTNING_PATH = 'M58,0 L18,58 L44,58 L34,100 L84,40 L54,40 Z';

// Two overlapping circles with opposite winding direction: under the
// default nonzero fill rule, the overlap cancels out (net winding 0),
// punching a "bite" out of the outer circle. Since the inner circle is
// offset rather than centered, the bite exits through the outer circle's
// edge instead of leaving a symmetric ring, producing a crescent.
const CRESCENT_K = 0.5523;
const crescentOuter = (cx: number, cy: number, r: number) => {
  const k = r * CRESCENT_K;
  return `M ${cx - r},${cy} C ${cx - r},${cy - k} ${cx - k},${cy - r} ${cx},${
    cy - r
  } C ${cx + k},${cy - r} ${cx + r},${cy - k} ${cx + r},${cy} C ${cx + r},${
    cy + k
  } ${cx + k},${cy + r} ${cx},${cy + r} C ${cx - k},${cy + r} ${cx - r},${
    cy + k
  } ${cx - r},${cy} Z`;
};
const crescentInnerReversed = (cx: number, cy: number, r: number) => {
  const k = r * CRESCENT_K;
  return `M ${cx - r},${cy} C ${cx - r},${cy + k} ${cx - k},${cy + r} ${cx},${
    cy + r
  } C ${cx + k},${cy + r} ${cx + r},${cy + k} ${cx + r},${cy} C ${cx + r},${
    cy - k
  } ${cx + k},${cy - r} ${cx},${cy - r} C ${cx - k},${cy - r} ${cx - r},${
    cy - k
  } ${cx - r},${cy} Z`;
};
export const CRESCENT_PATH = `${crescentOuter(
  46,
  50,
  44,
)} ${crescentInnerReversed(66, 50, 40)}`;

export const SHIELD_PATH =
  'M50,2 L90,16 L90,44 C90,74 72,90 50,98 C28,90 10,74 10,44 L10,16 Z';

export const SPADE_PATH =
  'M50,8 C22,32 4,54 4,70 C4,86 20,92 33,83 C36,89 34,94 26,100 L74,100 C66,94 64,89 67,83 C80,92 96,86 96,70 C96,54 78,32 50,8 Z';

export const CLUB_LOBE = (cx: number, cy: number, r: number) => {
  const k = 0.5523 * r;
  return `M ${cx - r},${cy} C ${cx - r},${cy - k} ${cx - k},${cy - r} ${cx},${
    cy - r
  } C ${cx + k},${cy - r} ${cx + r},${cy - k} ${cx + r},${cy} C ${cx + r},${
    cy + k
  } ${cx + k},${cy + r} ${cx},${cy + r} C ${cx - k},${cy + r} ${cx - r},${
    cy + k
  } ${cx - r},${cy} Z`;
};
export const CLUB_PATH = `${CLUB_LOBE(50, 32, 22)} ${CLUB_LOBE(
  28,
  62,
  20,
)} ${CLUB_LOBE(72, 62, 20)} M42,58 L58,58 L68,98 L32,98 Z`;

export const SPEECH_BUBBLE_RECT_PATH =
  'M20,8 L80,8 A12,12 0 0 1 92,20 L92,54 A12,12 0 0 1 80,66 L46,66 L28,88 L34,66 L20,66 A12,12 0 0 1 8,54 L8,20 A12,12 0 0 1 20,8 Z';

export const SPEECH_BUBBLE_OVAL_PATH =
  'M5,38 A45,32 0 1 1 95,38 A45,32 0 1 1 22,63 L10,85 L18,60 A45,32 0 0 1 5,38 Z';

export const EGG_PATH =
  'M50,2 C74,2 90,34 90,60 C90,86 72,99 50,99 C28,99 10,86 10,60 C10,34 26,2 50,2 Z';

export const LEAF_PATH = 'M50,4 C82,20 96,50 50,96 C4,50 18,20 50,4 Z';

export const CROWN_PATH = 'M8,92 L8,42 L27,58 L50,20 L73,58 L92,42 L92,92 Z';

export const PIN_PATH =
  'M50,98 C34,74 8,54 8,34 A42,42 0 1 1 92,34 C92,54 66,74 50,98 Z';

export const PIN_HOLLOW_PATH = `${PIN_PATH} ${(() => {
  const r = 14;
  const cx = 50;
  const cy = 34;
  const k = 0.5523 * r;
  return `M ${cx - r},${cy} C ${cx - r},${cy - k} ${cx - k},${cy - r} ${cx},${
    cy - r
  } C ${cx + k},${cy - r} ${cx + r},${cy - k} ${cx + r},${cy} C ${cx + r},${
    cy + k
  } ${cx + k},${cy + r} ${cx},${cy + r} C ${cx - k},${cy + r} ${cx - r},${
    cy + k
  } ${cx - r},${cy} Z`;
})()}`;

export const HOURGLASS_PATH =
  'M14,8 L86,8 L86,20 L54,50 L86,80 L86,92 L14,92 L14,80 L46,50 L14,20 Z';

export const CHECKMARK_PATH = 'M8,52 L32,76 L92,12 L80,2 L32,52 L20,40 Z';

export const SEAL_LOBES = 14;
